import {
  breakpoint,
  BodyText,
  Field,
  FormDropDown,
  Toggle,
  TextArea,
} from '@grays-npm/component-library';
import { useState } from 'react';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import { PageLayout, LoadingButton } from '../../components';
import { useForm } from '../../components/Form';
import { ApiError } from '../../services/apiError';
import {
  RegisterToSellRequestType,
  RegisterToSellReturnType,
  submitRegisterToSell,
} from '../../services/registerToSell';
import { useCMSData } from '../../state/content-manager';
import { StyledHeading as Heading } from '../styles';

const StyledHeading = styled(Heading)`
  margin-top: ${({ theme }) => theme.spacings[5].rem};
  margin-bottom: ${({ theme }) => theme.spacings[5].rem};

  ${breakpoint('md')`
      margin-top: 0;
  `};
`;

const StyledBody = styled(BodyText)`
  margin-bottom: ${({ theme }) => theme.spacings[5].rem};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: ${({ theme }) => theme.spacings[4].rem};
  margin-top: ${({ theme }) => theme.spacings[4].rem};

  ${breakpoint('sm')`
      grid-template-columns: 1fr 1fr;
  `};
`;

const StyledFormDropDown = styled(FormDropDown)`
  margin-bottom: ${({ theme }) => theme.spacings[4].rem};
`;

const Form = styled.form`
  max-width: 550px;
`;

const LegalText = styled(BodyText)`
  margin-bottom: ${({ theme }) => theme.spacings[4].rem};
`;

const nameValidation = {
  required: {
    value: true,
    message: 'Please enter your name',
  },
  pattern: {
    value: '^[A-Za-z]*$',
    message:
      "You're not allowed to use special characters or numbers in your name.",
  },
};

type SellerDetails = {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  state: string;
  category: string;
  subCategory: string;
  tellUsMore: string;
  businessType: string;
};

type RegisterToSellProps = {
  title?: string;
};

export const RegisterToSell: React.FC<RegisterToSellProps> = ({ title }) => {
  const { sellersCategories, statesAndTerritories, registerToSell } =
    useCMSData();
  const [isApiSuccess, setIsApiSuccess] = useState(false);
  const toggleValues = [{ value: 'Private' }, { value: 'Business' }];

  const registerToSellMutation = useMutation<
    RegisterToSellReturnType,
    ApiError,
    RegisterToSellRequestType
  >((registerToSellRequest) => submitRegisterToSell(registerToSellRequest), {
    onSuccess: () => setIsApiSuccess(true),
  });

  const {
    handleSubmit,
    handleChange,
    // handleChanges,
    data: sellerDetails,
    errors,
  } = useForm<SellerDetails>({
    validations: {
      firstName: nameValidation,
      lastName: nameValidation,
      email: {
        pattern: {
          value: '^[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$',
          message: 'Please enter a valid email address',
        },
      },
      mobileNumber: {
        required: {
          value: true,
          message: 'Please enter your mobile number',
        },
      },
      state: {
        required: {
          value: true,
          message: 'Please select your state',
        },
      },
      category: {
        required: {
          value: true,
          message: 'Please select a category',
        },
      },
      subCategory: {
        required: {
          value: true,
          message: 'Please select a sub category',
        },
      },
      tellUsMore: {
        required: {
          value: true,
          message: 'Please provide more information',
        },
        pattern: {
          value: '.{20,}',
          message: 'Please provide more information',
        },
      },
    },
    initialValues: {
      businessType: 'Private',
    },
    onSubmit: () => {
      const submitData = {
        firstName,
        lastName,
        email,
        mobileNumber,
        state: statesAndTerritories.find((x) => x.long === state)?.short || '',
        category: subCategory,
        tellUsMore,
        businessType,
      };
      registerToSellMutation.mutate(submitData);
    },
  });

  const {
    firstName,
    lastName,
    email,
    mobileNumber,
    state,
    category,
    subCategory,
    tellUsMore,
    businessType,
  } = sellerDetails;

  const subCategories = sellersCategories.find(
    (x) => x.category === category
  )?.subCategories;

  return (
    <PageLayout>
      <Form onSubmit={handleSubmit}>
        <StyledHeading size={4}>{title}</StyledHeading>
        <StyledBody>
          Please provide some details about you and the assets you're planning
          on selling.
        </StyledBody>
        <Toggle
          initialValue={businessType}
          options={toggleValues}
          onSelect={(value) => handleChange('businessType', value)}
          tabIndex={0}
        />
        <FieldGrid>
          <Field
            isCompact
            // value={firstName}
            errorMessage={errors.firstName}
            required
            label="First Name"
            onChange={(e) => handleChange('firstName', e.target.value)}
          />
          <Field
            isCompact
            // value={lastName}
            errorMessage={errors.lastName}
            required
            label="Last Name"
            onChange={(e) => handleChange('lastName', e.target.value)}
          />
          <Field
            isCompact
            // value={email}
            errorMessage={errors.email}
            required
            label="Email"
            onChange={(e) => handleChange('email', e.target.value)}
          />
          <Field
            isCompact
            // value={mobileNumber}
            errorMessage={errors.mobileNumber}
            required
            label="Mobile Number"
            onChange={(e) => handleChange('mobileNumber', e.target.value)}
            type="number"
            pattern="\d*"
          />
          <StyledFormDropDown
            isCompact
            errorMessage={errors.state}
            required
            label="Location"
            selectedOption={state}
            onSelect={(value) => handleChange('state', value)}
            options={statesAndTerritories.map((value) => ({
              label: value.long,
              value: value.long,
            }))}
          />
          <div></div>
          <StyledFormDropDown
            isCompact
            errorMessage={errors.category}
            required
            options={sellersCategories.map((x) => ({
              label: x.category,
              value: x.category,
            }))}
            label="Select a category"
            selectedOption={category}
            onSelect={(value) => {
              handleChange('category', value, {
                subCategory: value === 'Other' ? 'Other' : undefined,
              });
            }}
          />

          <StyledFormDropDown
            isCompact
            errorMessage={errors.subCategory}
            required
            options={subCategories?.map((x) => ({ label: x, value: x })) || []}
            label="Select a sub category"
            disabled={!category || category === 'Other'}
            selectedOption={subCategory}
            onSelect={(value) => handleChange('subCategory', value)}
          />
        </FieldGrid>
        <TextArea
          isCompact
          // value={tellUsMore}
          errorMessage={errors.tellUsMore}
          required
          label="Tell us about what you have to sell"
          onChange={(e) => handleChange('tellUsMore', e.target.value)}
        />
        <LegalText size={'xs'}>
          <span
            dangerouslySetInnerHTML={{ __html: registerToSell.privacyPolicy }}
          />
        </LegalText>
        <LoadingButton
          onClick={() => ''}
          onClose={() => ''}
          size="medium"
          disabled={registerToSellMutation.isLoading || isApiSuccess}
          isLoading={registerToSellMutation.isLoading}
          isSuccess={isApiSuccess}
          successText={'Submitted!'}
        >
          Submit enquiry
        </LoadingButton>
      </Form>
    </PageLayout>
  );
};
